import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard as DashboardIcon, 
  Receipt as ReceiptIcon, 
  Upload as UploadIcon, 
  BarChart as BarChartIcon,
  Assessment as AssessmentIcon,
  Rule as RuleIcon,
  ExitToApp as LogoutIcon,
  AccountCircle as AccountIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Link as LinkIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawerWidth = sidebarOpen ? 260 : 70;
  
  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices' },
    { text: 'Upload', icon: <UploadIcon />, path: '/upload' },
    { text: 'Chart View', icon: <AssessmentIcon />, path: '/charts' },
    { text: 'API Uses', icon: <BarChartIcon />, path: '/api-uses' },
    { text: 'Rule Management', icon: <RuleIcon />, path: '/rules' },
    { text: 'Alias Management', icon: <LinkIcon />, path: '/alias-management' },
    { text: 'Activity Logs', icon: <HistoryIcon />, path: '/activity-logs' },
  ];

  const drawer = (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        backgroundColor: '#fff',
        borderRight: '1px solid #e0e0e0'
      }}
    >
      <Toolbar sx={{ 
        backgroundColor: theme.palette.primary.main, 
        color: '#fff',
        minHeight: '64px',
        pl: 2,
        pr: 1,
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <Collapse orientation="horizontal" in={sidebarOpen}>
          <Typography 
            variant="h6" 
            noWrap 
            component="div"
            sx={{ 
              fontWeight: 600,
              fontSize: '1.2rem',
              mr: 1
            }}
          >
            LLM Management
          </Typography>
        </Collapse>
        <IconButton
          onClick={toggleSidebar}
          sx={{ 
            color: '#fff',
            p: 0.5
          }}
        >
          {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Toolbar>
      
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        px: 1,
        py: 2
      }}>
        <List>
          {menuItems.map((item) => (
            <ListItem 
              button 
              key={item.text}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                mb: 1,
                backgroundColor: location.pathname === item.path 
                  ? 'rgba(63, 81, 181, 0.1)' 
                  : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                minHeight: 48,
                px: 1.5
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path 
                    ? theme.palette.primary.main 
                    : 'text.secondary',
                  minWidth: '32px',
                  mr: sidebarOpen ? 1.5 : 'auto',
                  justifyContent: 'center'
                }}
              >
                {item.icon}
              </ListItemIcon>
              <Collapse orientation="horizontal" in={sidebarOpen}>
                <ListItemText 
                  primary={item.text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    }
                  }}
                />
              </Collapse>
            </ListItem>
          ))}
        </List>
      </Box>
      
      <Box sx={{ px: 1, pb: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <ListItem 
          button
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            minHeight: 48,
            px: 1.5
          }}
        >
          <ListItemIcon
            sx={{
              color: 'text.secondary',
              minWidth: '32px',
              mr: sidebarOpen ? 1.5 : 'auto',
              justifyContent: 'center'
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <Collapse orientation="horizontal" in={sidebarOpen}>
            <ListItemText 
              primary="Logout" 
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: 500,
                }
              }}
            />
          </Collapse>
        </ListItem>
        
        {user && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 2, 
            p: 1.5, 
            borderRadius: 2,
            backgroundColor: 'background.default'
          }}>
            <AccountIcon 
              sx={{ 
                mr: sidebarOpen ? 1.5 : 'auto',
                color: 'action.active',
                fontSize: '1.5rem',
                minWidth: '32px'
              }} 
            />
            <Collapse orientation="horizontal" in={sidebarOpen}>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {user.name || user.username || user.email || 'User'}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                >
                  Account
                </Typography>
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#fff',
          color: 'text.primary',
          zIndex: theme.zIndex.drawer - 1,
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600
            }}
          >
            {location.pathname === '/' && 'Dashboard'}
            {location.pathname === '/invoices' && 'Invoices'}
            {location.pathname === '/upload' && 'Upload Invoice'}
            {location.pathname === '/charts' && 'Chart View'}
            {location.pathname === '/alias-management' && 'Alias Management'}
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ mr: 2, fontWeight: 500 }}>
                Welcome, {user.name || user.username || user.email?.split('@')[0] || 'User'}
              </Typography>
              <Tooltip title="Logout">
                <IconButton 
                  color="inherit" 
                  onClick={handleLogout}
                  size="large"
                  sx={{ color: 'text.primary' }}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ 
          width: { sm: drawerWidth }, 
          flexShrink: { sm: 0 },
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: theme.zIndex.drawer,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
        aria-label="navigation"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid #e0e0e0',
              zIndex: theme.zIndex.drawer
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid #e0e0e0',
              backgroundColor: '#fff',
              boxShadow: 3,
              zIndex: theme.zIndex.drawer,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` }, // Add left margin to push content past sidebar
          marginTop: '64px', // Account for fixed AppBar height
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: theme.palette.background.default,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;